import { useEffect, useRef } from 'react';
import { InputNumber } from 'antd';

// Función para agregar separador de miles
export const addThousandSeparator = (value) => {
  const separatorRegex = /\B(?=(\d{3})+(?!\d))/g; // Regex para separar 
  return value.replace(separatorRegex, ',');
};

// Limpia y formatea un número según el tipo especificado
const cleanAndFormatNumber = (value, numberType) => {
  const nonNumericRegex = /[^\d.-]/g; // Regex para seleccionar los caracteres no numéricos
  const cleanedValue = value.replace(nonNumericRegex, '').replace(/-+/g, '-');

  if (cleanedValue === '' || cleanedValue === '-') return cleanedValue;

  return (
    numberType === 'positive' && cleanedValue[0] === '-' ? cleanedValue.slice(1) : // Eliminamos el signo '-' si el tipo de número es positivo
      numberType === 'negative' && cleanedValue[0] !== '-' ? `-${cleanedValue}` : // Agregamos el signo '-' si el tipo de número es negativo
        cleanedValue
  );
};

/**
 * Componente: Input numérico con formato personalizado.
 * Se extienden las propiedades del componente [InputNumber](https://ant.design/components/input-number#api) de Ant Design.
 *
 * @param precision Número de decimales permitidos.
 * @param numberType Tipo de número permitido: 'positive' | 'negative' | 'all' (default: 'all').
 * @param thousandsSeparator Establece si se debe usar separador de miles en la presentación del número (default: false).
 * @param fixedPrefix Establece si el prefijo debe ser fijo o depender del valor del input (default: true).
 * @param emptyValueOutput Define el valor que se debe notificar al componente padre cuando el campo esté vacío: 'emptyString' | 'null' (default: 'null').
 * 
 * @example
 * // Admite solo números positivos, con máximo 2 decimales
 * // Cuenta con un separador de miles para mejorar la legibilidad
 * // Incluye un prefijo de '$' que se oculta cuando no hay un valor ingresado
 * <InputNumber
 *   precision={2}
 *   numberType='positive'
 *   thousandsSeparator
 *   prefix='$'
 *   fixedPrefix={false}
 * />
 */
export const InputNumberPlus = ({
  numberType = 'all',
  thousandsSeparator = false,
  fixedPrefix = true,
  emptyValueOutput = 'null',
  prefix,
  precision,
  onChange,
  onBlur,
  style,
  ...inputProps
}) => {
  const lastValueRef = useRef(inputProps.value); // Referencia para almacenar el último valor del input actualizado internamente

  const handleFormatter = (value, info) => {
    // Si el usuario está escribiendo, preserva los ceros a la izquierda usando el valor limpio de info.input, de lo contrario, se usa value que los elimina
    const currentValue = info.userTyping ? cleanAndFormatNumber(info.input, numberType) : value?.toString() ?? '';
    if (currentValue === '' || currentValue === '-') return currentValue;

    // Se redondea el número a la precisión especificada
    let newCurrentValue = Number(currentValue);
    if (precision !== undefined) {
      const factor = Math.pow(10, precision);
      newCurrentValue = Math.round(newCurrentValue * factor) / factor;
    }

    const [integerPart, decimalPart] = newCurrentValue.toString().split('.');

    // Formateamos la parte entera del número, añadiendo un separador de miles si está habilitado
    const formattedIntegerPart = thousandsSeparator ? addThousandSeparator(integerPart) : integerPart;
    let formattedDecimalPart = '';
    if (!info.userTyping) {
      formattedDecimalPart = precision !== undefined
        ? (precision > 0 ? (decimalPart ? `.${decimalPart.slice(0, precision)}` : '.' + '0'.repeat(precision)) : '')
        : (decimalPart ? `.${decimalPart}` : '');

      return `${formattedIntegerPart}${formattedDecimalPart}`;
    }

    formattedDecimalPart = precision !== undefined
      ? (precision > 0 ? (decimalPart ? `.${decimalPart.slice(0, precision)}` : '') : '')
      : (decimalPart ? `.${decimalPart}` : '');

    return `${formattedIntegerPart}${formattedDecimalPart}`;
  };

  // Función para limpiar y formatear el número antes de procesarlo
  const handleParser = (value) => value ? cleanAndFormatNumber(value, numberType) : '';

  // Función para notificar al componente padre sobre el cambio en el valor
  const handleChange = (value) => {
    lastValueRef.current = value;
    onChange?.(value);
  };

  // Se ejecuta cuando el usuario pierde el foco del input
  const handleBlur = (e) => {
    const cleanedValue = cleanAndFormatNumber(e.target.value, numberType);

    const emptyValueOutputMap = {
      emptyString: '',
      null: null,
    };

    // Si cleanedValue es '', se notifica al componente padre con el valor configurado en emptyValueOutput
    if (cleanedValue === '') handleChange(emptyValueOutputMap[emptyValueOutput]);

    onBlur?.(e);
  };

  // Determina si se debe mostrar el prefijo, dependiendo de la condición de fixedPrefix y la existencia del valor en inputProps
  const prefixToShow = prefix
    ? (fixedPrefix || inputProps.value) ? prefix : '\u200B' // '\u200B': espacio en blanco cero ancho, se utiliza en el prefix para evitar la pérdida del foco del input 
    : undefined;

  // Ejecuta handleChange si el valor del input se actualiza externamente
  useEffect(() => {
    if (inputProps.value !== undefined && inputProps.value !== lastValueRef.current) {
      handleChange(inputProps.value);
    }
  }, [inputProps.value]);

  return (
    <InputNumber
      prefix={prefixToShow}
      onChange={handleChange}
      onBlur={handleBlur}
      formatter={handleFormatter}
      controls={false} // Por defecto se desactivan los controles del input
      parser={handleParser}
      precision={precision}
      style={{ width: '100%', ...style }}
      {...inputProps}
    />
  );
};